import React, {useEffect, useState} from 'react';
import {useForm, useFieldArray} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import classNames from 'classnames';
import {useNavigate, useParams} from 'react-router-dom';

import {SelectFieldHookForm} from '../ui/SelectField';
import Button from '../ui/Button';
import InputField from '../ui/InputField';

import {difficultyLevelOptions, validations} from '../../utils/constants';
import {
  useAddQuestionMutation,
  useGetQuestionDetailsQuery,
  useUpdateQuestionMutation,
} from '../../api/candidateTest';
import {invalidateQueryV2} from '../../services/reactQuery';
import SubjectDropdown from '../common/SubjectDropdown';
import FormItem from '../jobForm/FormItem';
import {isEmpty} from 'lodash';

const questionSchema = yup.object().shape({
  testSubjectId: yup.object().nullable().required('Subject is required'),
  questions: yup.array().of(
    yup.object().shape({
      question: yup
        .string()
        .required('Question text is required')
        .matches(validations.notAllowedSpace, 'Question text is required'),
      optionA: yup
        .string()
        .required('OptionA text is required')
        .matches(validations.notAllowedSpace, 'OptionA text is required'),
      optionB: yup
        .string()
        .required('OptionB text is required')
        .matches(validations.notAllowedSpace, 'OptionB text is required'),
      optionC: yup
        .string()
        .required('OptionC text is required')
        .matches(validations.notAllowedSpace, 'OptionC text is required'),
      optionD: yup
        .string()
        .required('OptionD text is required')
        .matches(validations.notAllowedSpace, 'OptionD text is required'),
      answer: yup.array().of(yup.string()).min(1, 'Please select one answer'),
      difficulty: yup.object().nullable().required('Difficulty is required'),
    }),
  ),
});

const setInitialFormData = (questionDetails) => {
  if (!questionDetails) {
    return {
      testSubjectId: '',
      questions: [
        {
          question: '',
          answer: [],
        },
      ],
    };
  }

  const {options, question, answer, difficulty, subject} = questionDetails;

  const updatedQuestionDetails = {
    ...options,
    question,
    answer: answer.split(','),
    difficulty: difficultyLevelOptions.find((x) => x.value == difficulty),
  };

  const payload = {
    subject: {value: subject, label: subject},
    questions: [updatedQuestionDetails],
  };

  return payload;
};

const initialFormValue = {
  subject: '',
  questions: [{question: '', answer: []}],
};

/**
 *
 * Component AddQuestions
 */
const AddQuestions = ({editMode = false}) => {
  const navigate = useNavigate();
  const {questionId} = useParams();

  const {data: questionDetailsResponse, isFetching: isQuestionDetailsLoading} =
    useGetQuestionDetailsQuery(questionId);
  const questionDetails = questionDetailsResponse?.data[0] || null;

  const {mutate: addQuestionMutation, isLoading: isAddQuestionLoading} =
    useAddQuestionMutation();
  const {mutate: updateQuestionMutation, isLoading: isUpdateQuestionLoading} =
    useUpdateQuestionMutation();

  const [isQuestionEmpty, setIsQuestionEmpty] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(questionSchema),
    mode: 'all',
    defaultValues: initialFormValue,
  });

  useEffect(() => {
    return () => reset();
  }, []);

  // set form data after getting current question details
  useEffect(() => {
    if (questionDetails && !isQuestionDetailsLoading) {
      const payload = setInitialFormData(questionDetails);
      reset(payload);
    }
  }, [questionDetails, isQuestionDetailsLoading]);

  const {
    fields = [],
    append,
    remove,
  } = useFieldArray({name: 'questions', control});

  const handleFormSubmission = (values) => {
    const {
      testSubjectId: {value: testSubjectId},
      questions,
    } = values;

    const updatedQuestions = questions.map((quesitonDetails) => {
      const {
        answer,
        difficulty: {value: difficulty},
        optionA,
        optionB,
        optionC,
        optionD,
        question,
      } = quesitonDetails;
      return {
        difficulty: +difficulty,
        answerType: answer.length <= 1 ? 1 : 2,
        answer: answer.join(','),
        question,
        options: {
          optionA,
          optionB,
          optionC,
          optionD,
        },
      };
    });

    const payload = {
      testSubjectId,
      questions: updatedQuestions,
    };

    const mutationOptions = {
      onSuccess: () => {
        toast.success(
          `Question ${editMode ? 'updated' : 'added'} Successfully`,
        );
        reset();
        invalidateQueryV2(['questions']);
        navigate('/questions');
      },
      onError: (data) => toast.error(data.response.data.message),
    };

    if (!isEmpty(fields)) {
      setIsQuestionEmpty(false);
      if (questionId) {
        // edit mode
        const {questions, ...restPayload} = payload;
        const updatedPayload = {
          ...restPayload,
          questionId,
          ...questions[0],
        };

        updateQuestionMutation(updatedPayload, mutationOptions);
      } else {
        addQuestionMutation(payload, mutationOptions);
      }
    } else {
      setIsQuestionEmpty(true);
    }
  };

  return (
    <>
      <div className="px-8 py-6">
        <div className="flex items-center justify-center gap-2">
          <h3 className="text-2xl font-bold">
            {questionId ? 'Edit Question' : 'Add Questions'}
          </h3>
        </div>
        <form
          className="mt-6 mx-auto max-w-[1200px] w-full"
          onSubmit={handleSubmit(handleFormSubmission)}
        >
          <div className="bg-white p-6 border border-gray-200 rounded-md">
            <div className="grid grid-cols-[49%_49%] gap-[2%] mb-5">
              <div className="flex flex-col">
                <FormItem label="Subjects" htmlFor="testSubjectId">
                  <SubjectDropdown
                    name="testSubjectId"
                    control={control}
                    className="w-full"
                    placeholder="Select Subject"
                    error={errors.testSubjectId?.message}
                    setValueInForm={(value) => setValue('testSubjectId', value)}
                    questionDetails={questionDetails}
                    queryEnabled={!!questionDetails}
                    optionsWithId
                  />
                </FormItem>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 mt-3 flex flex-col gap-2 border border-gray-200 rounded-md">
            <div className="text-right">
              {!questionId && (
                <Button
                  onClick={() => {
                    setIsQuestionEmpty(false);
                    append(initialFormValue.questions);
                  }}
                >
                  Add Question
                </Button>
              )}
            </div>
            <div className="flex flex-col gap-5 py-5">
              {fields.map((_, i) => (
                <div
                  key={i}
                  className={classNames('flex flex-col gap-5 pb-5', {
                    'border-t border-black pt-5': !editMode,
                  })}
                >
                  <div>
                    <div className="flex justify-between font-semibold mb-2">
                      <label>
                        <span>Question - {i + 1} </span>
                      </label>
                      <button
                        type="button"
                        className={classNames('text-error underline', {
                          invisible: !!questionId,
                        })}
                        onClick={() => remove(i)}
                      >
                        Remove
                      </button>
                    </div>

                    <InputField
                      name={`questions[${i}]question`}
                      {...register(`questions.${i}.question`)}
                      placeholder={`Enter question ${i + 1}`}
                      error={errors.questions?.[i]?.question?.message}
                    />
                  </div>
                  <div className="grid grid-cols-[49%_49%] gap-[2%]">
                    <div className="flex flex-col w-full">
                      <div className="flex items-center gap-3 mb-2.5">
                        <label className="font-semibold ">Option 1</label>
                        <input
                          type="checkbox"
                          {...register(`questions.${i}.answer`)}
                          className="mr-4 inline-block h-4 w-4"
                          value="optionA"
                          tabIndex="-1"
                        />
                      </div>
                      <InputField
                        name={`questions[${i}]optionA`}
                        {...register(`questions.${i}.optionA`)}
                        type="text"
                        placeholder="Enter option 1"
                        error={errors.questions?.[i]?.optionA?.message}
                      />
                    </div>

                    <div className="flex flex-col">
                      <div className="flex items-center gap-3 mb-2.5">
                        <label className="font-semibold ">Option 2</label>
                        <input
                          type="checkbox"
                          {...register(`questions.${i}.answer`)}
                          className="mr-4 inline-block h-4 w-4"
                          value="optionB"
                          tabIndex="-1"
                        />
                      </div>
                      <InputField
                        name={`questions[${i}]optionB`}
                        {...register(`questions.${i}.optionB`)}
                        type="text"
                        placeholder="Enter option 2"
                        error={errors.questions?.[i]?.optionB?.message}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-[49%_49%] gap-[2%]">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-3 mb-2.5">
                        <label className="font-semibold ">Option 3</label>
                        <InputField
                          type="checkbox"
                          {...register(`questions.${i}.answer`)}
                          className="mr-4 inline-block h-4 w-4"
                          value="optionC"
                          tabIndex="-1"
                        />
                      </div>
                      <InputField
                        name={`questions[${i}]optionC`}
                        {...register(`questions.${i}.optionC`)}
                        type="text"
                        placeholder="Enter option 3"
                        error={
                          errors.questions?.[i]?.optionC?.message ||
                          errors.questions?.[i]?.answer?.message
                        }
                      />
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-3 mb-2.5">
                        <label className="font-semibold ">Option 4</label>
                        <input
                          type="checkbox"
                          {...register(`questions.${i}.answer`)}
                          className="mr-4 inline-block h-4 w-4"
                          value="optionD"
                          tabIndex="-1"
                        />
                      </div>
                      <InputField
                        name={`question[${i}]optionD`}
                        {...register(`questions.${i}.optionD`)}
                        type="text"
                        placeholder="Enter option 4"
                        error={errors.questions?.[i]?.optionD?.message}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="font-semibold mb-2.5">Difficulty</label>
                    <SelectFieldHookForm
                      placeholder="select Difficulty"
                      control={control}
                      className="w-full"
                      name={`questions.${i}.difficulty`}
                      options={difficultyLevelOptions}
                      error={errors.questions?.[i]?.difficulty?.message}
                    />
                  </div>
                </div>
              ))}
            </div>
            {isQuestionEmpty && (
              <div className="text-error text-sm p-1 flex justify-center">
                Add at least one question
              </div>
            )}
            <div className="text-center">
              <Button
                type="submit"
                className="w-[200px]"
                loading={isAddQuestionLoading || isUpdateQuestionLoading}
              >
                Submit
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddQuestions;
